import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';

import { UserApiService } from 'src/app/api/services/user-api.service';
import { UserauthApiService } from 'src/app/api/services/userauth-api.service';
import { UserAuthService } from 'src/app/auth/services/user-auth.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { UserFieldsComponent } from 'src/app/settings/components/user-fields/user-fields.component';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import { compareIgnoringFields } from 'src/app/shared/helpers/object.helper';

@Component({
  selector: 'user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false,
})
export class UserDetailsComponent
  extends BaseEditableComponent<any>
  implements OnInit
{
  username;
  user: any = {};
  userPassword: any = {};
  loading = false;
  formError = null;
  apiError = null;
  activationLink: string;

  isUserAdmin = this.userStateService.isUserAdmin();
  showChangePassword = false;
  editingYourself;

  @ViewChild('userForm')
  public userForm: NgForm;

  @ViewChild(UserFieldsComponent) userFieldsComponent: UserFieldsComponent;

  actions: ActionsMenu = {};
  pageActions: ActionsMenu = {};

  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    private userStateService: UserStateService,
    private userAuthService: UserAuthService,
    private userApiService: UserApiService,
    private userauth: UserauthApiService,
    private tracker: TrackerService,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.processErrorCode = processErrorCodeAdapter.createCallable();
  }

  ngOnInit() {
    this.username =
      this.stateService.params.username || this.userStateService.getUsername();
    this.editingYourself = this.userStateService.checkUsername(this.username);

    this.actions = {
      saveAction: {
        id: 'saveButton',
        label: 'Save',
        action: () => this.save(),
        disabled: this.loading || !(this.isUserAdmin || this.editingYourself),
      },
      cancelAction: {
        id: 'cancelButton',
        label: 'Cancel',
        action: () => {
          this.bypassUnsavedChanges();
          this.stateService.go('apps.settings.user-list');
        },
      },
    };

    this.pageActions = {
      deleteAction: {
        id: 'deleteButton',
        label: 'Delete User',
        icon: 'trash',
        action: () => this.deleteUser(),
        disabled: !this.isUserAdmin,
      },
      moreOptions: [
        {
          id: 'changePasswordButton',
          label: 'Change Password',
          action: () => this.toggleChangePassword(),
          disabled: !this.editingYourself,
        },
      ],
    };

    this.loading = true;
    this.userApiService
      .get(this.username)
      .then((user) => {
        this.user = user;
        this.oldObject = this.cloneDeep(this.user);
      })
      .catch((error) => {
        this._showErrorMessage('load', error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  canDeactivate(): boolean {
    const isEqualWithoutRoles = compareIgnoringFields(
      this.userFieldsComponent.editUser,
      this.oldObject,
      ['roles'],
    );
    const hasEqualRoles = this.user.roles.every((role) =>
      this.oldObject.roles.includes(role),
    );

    return isEqualWithoutRoles && hasEqualRoles;
  }

  deleteUser() {
    let title, message, confirmButtonText;

    if (this.user.ssouserProvisioningEnabled) {
      title = 'SSO Provisioning Enabled';
      message =
        "Deleting a user in Rise Vision won't prevent them from logging in if they remain active in your identity provider (IdP). To remove access permanently, delete the user in both Rise Vision and your IdP.";
      confirmButtonText = 'Delete From Rise Vision';
    } else {
      title = 'Deleting User';
      message = 'Are you sure you want to delete this user?';
      confirmButtonText = 'Delete Forever';
    }

    this.modalService
      .confirmDanger(title, message, confirmButtonText)
      .then(() => {
        this.loading = true;

        this.userApiService
          .delete(this.username)
          .then(() => {
            this.tracker.userEvent(
              'User Deleted',
              this.userStateService.getUsername(),
              this.userStateService.checkUsername(this.username),
              {
                deletedUserId: this.username,
              },
            );

            if (this.userStateService.checkUsername(this.username)) {
              this.userAuthService.signOut();
            }

            this.bypassUnsavedChanges();
            this.stateService.go('apps.settings.user-list');
          })
          .catch((error) => {
            this.resetUnsavedChanges();
            this._showErrorMessage('delete', error);
          })
          .finally(() => {
            this.loading = false;
          });
      });
  }

  _setPasswordError(error: any) {
    if (this.userForm.controls['currentPassword']) {
      this.userForm.controls.currentPassword.setErrors(error);
    }
  }

  save() {
    const editedUser = this.userFieldsComponent.editUser;
    if (this.showChangePassword) {
      this._setPasswordError(null);
    }

    if (this.userForm.valid) {
      var changePasswordPromise = Promise.resolve({ item: '' });

      this.loading = true;

      if (this.showChangePassword) {
        changePasswordPromise = this.userauth.updatePassword(
          this.username,
          this.userPassword.currentPassword,
          this.userPassword.newPassword,
        );
        changePasswordPromise
          .then((resp) => {
            this.userPassword = {};
            this.showChangePassword = false;

            return resp;
          })
          .catch((err) => {
            var newError = err.result.error;

            if (newError.code === 409) {
              this._setPasswordError({ currentPasswordNotValid: true });

              newError.changePassword = true;
            }
            return Promise.reject(newError);
          });
      }

      changePasswordPromise
        .then(() => {
          return this.userApiService.update(
            this.username,
            editedUser,
            this.userStateService.isUserAdmin(),
          );
        })
        .then((resp) => {
          if (this.userStateService.checkUsername(this.username)) {
            this.userStateService.updateUserProfile(resp.item);
          }
          this.oldObject = this.cloneDeep(this.user);
        })
        .catch((error) => {
          this._showErrorMessage('update', error);

          error = (error.result && error.result.error) || error;
          console.debug(error);
          if (error.changePassword) {
            this.apiError = error.message;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  toggleChangePassword() {
    this.showChangePassword = !this.showChangePassword;

    if (this.showChangePassword) {
      this._setPasswordError(null);
    }
  }

  _showErrorMessage(action, error) {
    this.formError = 'Failed to ' + action + ' User.';
    this.apiError = this.processErrorCode(error);
  }
}
